
'use client'
import { type FC, memo, useEffect, useMemo, useState } from 'react'
import styled, { css } from 'styled-components'
import { motion, type Variants } from 'motion/react'
import { usePageDesign, useTranslator } from '@/hooks'
import type { TypeCmsBaseHeader, TypeCmsVideo } from '@/schema'
import { createUnderline, designGrid, getImgTransition, getP50, getP90, getTransition, mq, staggerChildVariants, vw } from '@/styles'
import { Img } from './commons'
import { NAVBAR_HEIGHTS } from './commons/layout/Navbar'
import { VideoGeneric } from './commons/VideoGeneric'

type Props = {
  data: TypeCmsBaseHeader
  isPDP?: boolean
  isHome?: boolean
}

const onClickScrollDown = () => {
  window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })
}

export const Header: FC<Props> = memo(({ data, isPDP, isHome = false }) => {
  const { title, text_color, medium, subtitle, font_size, text_position } = data || {}
  const { setHeaderTextColor, setPageHasHeaderBgImage } = usePageDesign()
  const varTextColor = useMemo(() => `var(--color-${(!text_color || text_color === 'dark') ? 'text' : 'white'})`, [text_color])
  const { t } = useTranslator()

	const [videoLoaded, setVideoLoaded] = useState(false)


	let mediumData = medium
  // Image or video
	if(data.video_enabled) {
		// @ts-expect-error - TODO: LINTER improvement, do not affect
		mediumData = {
			type: 'video',
			alt: 'header video',
			platform: data.video_type,
			short_video: data.short_video,
			long_video: data.long_video,
		} as unknown as TypeCmsVideo
	}

  const onClick = () => {
    window.scrollTo({top: window.innerHeight, behavior: 'smooth'})
  }

  useEffect(() => {
    setHeaderTextColor(text_color)
    if (medium) setPageHasHeaderBgImage(true)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medium, text_color])


  return (
    <HeaderStyled $varTextColor={varTextColor} $font_size={font_size} $textPosition={text_position} $isPDP={isPDP} $isHome={isHome} variants={VARIANTS_PARENT} initial='initial' animate='animate' exit='exit'>
			{medium?.image && <Img className={videoLoaded ? 'video-loaded' : ''} key={medium?.id} media={medium.image} lazy={false} sizes='100vw'/>}
			{data.video_enabled ? <VideoGeneric data={mediumData as unknown as TypeCmsVideo} className='ld' setVideoLoaded={setVideoLoaded} /> : <></>}
      {/* {medium && <Img key={medium?.id} media={medium.image} lazy={false} sizes='100vw'/>} */}
      <div className='text_wrapper'>
        {title &&<motion.h1 variants={VARIANTS_TITLE}>{title}</motion.h1>}
        {subtitle && <motion.p variants={staggerChildVariants}>{subtitle}</motion.p>}
        {(isHome && data.image_size === 'full') &&
          <motion.button variants={staggerChildVariants} className='link' onClick={() => onClick()}>
            {t('project.home.header_link')}
            <span/>
          </motion.button>
        }
      </div>
      <button className='btn--scroll-down' aria-label={'Scroll'} onClick={onClickScrollDown}>
        <svg xmlns='http://www.w3.org/2000/svg' width='29' height='16' viewBox='0 0 29 16'><path d='M28 1L14 15 0 1' stroke={varTextColor} strokeWidth='.5' fill='none' /></svg>
      </button>
    </HeaderStyled>
  )
})

const HeaderStyled = styled(motion.header) <{ $varTextColor: string, $isPDP?: boolean, $isHome?: boolean, $font_size?: string, $textPosition?: string }>`
  ${designGrid({})}
  align-items: center;
  background-color: var(--color-creme);
  color: ${({ $varTextColor }) => $varTextColor};
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  position: relative;
  width: 100vw;
  width: 100dvw;

	img {
		z-index: 1;
	}

	figure {
		z-index: 2;
	}

	figure,
  img {
    ${getImgTransition()}
    height: 100%;
    left: 0;
    object-fit: cover;
    pointer-events: none;
    position: absolute;
    top: 0;
    user-select: none;
    width: 100%;
  }

  h1,
  h2,
  p,
  a,
  button {
    color: inherit;
    grid-column: 1 / span 6;
    z-index: 2;

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 12;
    }
  }

  h1 {
    ${({ $font_size }) => $font_size === 'big' ? css` ${getP90()}`: css`${getP50()}`}
    ${({ $isPDP }) => $isPDP && css`
      margin-bottom: auto;
      margin-right: auto;
      margin-top: ${vw(NAVBAR_HEIGHTS.mobile + 10, 'mobile')};

      ${mq.greaterThan('nexus7')} {
        margin-top: ${vw(NAVBAR_HEIGHTS.mobile + 10, 'tablet')};
      }

      ${mq.greaterThan('tablet')} {
        margin-top: ${vw(NAVBAR_HEIGHTS.desktop + 10, 'desktop')};
      }

      ${mq.greaterThan('desktop')} {
        margin-top: ${NAVBAR_HEIGHTS.desktop + 10}px;
      }
    `}
  }

  h1 + .link {
    margin-top: ${vw(30, 'mobile')};

    ${mq.greaterThan('tablet')} {
      margin-top: ${vw(40, 'desktop')};
    }
  }


  button.link {
    display: block;
    margin: 0;
    ${({ $varTextColor }) => createUnderline({ color: $varTextColor, reverse: true })}
  }

  p {
    ${getP50()}
    width: 100%;
    margin-top: ${vw(30, 'mobile')};
    margin-bottom: ${vw(90, 'desktop')};

    ${mq.greaterThan('tablet')} {
      margin-top: ${vw(40, 'desktop')};
      margin-bottom: ${vw(130, 'desktop')};
    }
  }

  .text_wrapper {
    display: flex;
    flex-flow: column;
    grid-area: 1 / 1 / span 1 / span 6;
    height: 100vh;
    justify-content: space-between;
    align-items: ${({ $textPosition }) => $textPosition === 'left' ? 'flex-start' : 'center'};

    ${({ $isHome, $isPDP }) => ($isHome || !$isPDP) && css`
      justify-content: center;
    `}
    ${mq.greaterThan('tablet')} {
      grid-area: 1 / 1 / span 1 / span 6;
      ${({ $isHome, $isPDP }) => ($isHome || !$isPDP) && css`
          grid-area: 1 / 1 / span 1 / span 12;
          justify-content: center;
      `}
    }

    &:has(.link) {
      gap: 20px;
    }
  }

  .btn--scroll-down {
    bottom: 0;
    left: 50%;
    margin-bottom: ${vw(40, 'mobile')};
    position: absolute;
    padding: 20px;
    transform: translateX(-50%);

    ${mq.greaterThan('nexus7')} {
      margin-bottom: ${vw(40, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      margin-bottom: ${vw(60, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      margin-bottom: 60px;
    }

    &:hover {
      svg {
        transform: translateY(6px);
      }
    }

    svg {
      height: ${vw(10, 'mobile')};
      transition: 250ms transform ease-out;
      width: ${vw(20, 'mobile')};

      ${mq.greaterThan('nexus7')} {
        height: ${vw(10, 'tablet')};
        width: ${vw(20, 'tablet')};
      }

      ${mq.greaterThan('tablet')} {
        height: ${vw(14, 'desktop')};
        width: ${vw(28, 'desktop')};
      }

      ${mq.greaterThan('desktop')} {
        height: 14px;
        width: 28px;
      }
    }
  }
`

export const VARIANTS_PARENT = {
  animate: {
    transition: {
      delay: .7,
      staggerChildren: 1,
    }
  }
}

export const VARIANTS_TITLE:Variants = {
  initial: {
    y: '20px',
    opacity: 0,
    transition: {
      ...getTransition()
    }
  },
  animate: {
    y: '0px',
    opacity: 1,
    transition: {
      ...getTransition(),
      ease: [.32, 0, .29, .99],
      duration: 1
    }
  },
  exit: {
    y: '20px',
    opacity: 0,
    transition: {
      ...getTransition(),
      duration: .5
    }
  },
}
